/*
* California Disclosure for authenticated users
*/

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Button, ButtonGroup, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import scrollToComponent from 'react-scroll-to-component';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import { isLevel3Report } from 'src/order/utils';
import Branding from '../../branding/branding';
import * as AppComp from '../../common/app-component-base';
import * as AppUtilities from '../../common/apputilities';
import { BootstrapColXl as Col } from '../../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../../common/formatters';
import { GridUtilities } from '../../common/grid/gridutilities';
import * as KendoWrappers from '../../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../../common/navigation';
import ApiEndpoints from '../../config/apiendpoints';
import { AppImages } from '../../config/images';
import * as ReduxQueries from '../../queries';
import * as ReportCharts from '../charts';
import * as ReportLogic from '../logic';
import * as ReportCommon from './../report-common';
import { Render as DisclosurePage1 } from './pages/disclosure-page-1';
import { Render as DisclosurePage2 } from './pages/disclosure-page-2';
import * as ReportComponents from './report-components';

import { isNullOrUndefined } from '../../utils/generalUtils';
import '../../branding/report-pdf.css';
import '../pdf.css';

export const DefaultReportDate = (): Date => moment('1/1/1900', 'MM/DD/YYYY').startOf('year').toDate();
export const MinimumReportDate = (): Date => moment('1/1/2000', 'MM/DD/YYYY').startOf('year').toDate();

export interface ICaliforniaDisclosureReportPdfProps extends AppComp.IAppComponentProps {
    association?: any;
    associationId?: any;
    currentOrder?: any;
    currentOrderChart?: any;
    inventory?: any;
    scenarios?: any;
    userResumes?: any;
    navigateTo?: any;
}

export interface ICaliforniaDisclosureReportPdfState extends AppComp.IAppComponentState {
    formChanges?: any;
    inventoryChanges?: any;
    reportPageCount: number;
    reportParameters?: any;
    specialAssessments?: any;
    additionalAssessments?: any;
    showModalMissingValues: boolean;
}

export const DisclosureFormSection = ({ title, className = '', ...rest }) => {
    return (
        <div className={`reserve-study-form-section ${className}`}>
            <KendoWrappers.KendoFormSection title={title}>
                {rest.children}
            </KendoWrappers.KendoFormSection>
        </div>
    );
};

export const DefaultLastReportDateSubtractInterval = 1;
export const DefaultLastReportDateSubtractPeriod = 'years';
export const DefaultLastReportDate = (): Date => moment().startOf('day').toDate();

class CaliforniaDisclosureReportPdf extends AppComp.AppComponent<ICaliforniaDisclosureReportPdfProps, ICaliforniaDisclosureReportPdfState> {
    public static readonly scrollToConfig = {
        align: 'top',
        ease: 'out-circ',
        duration: 550,
        offset: -55
    };

    public originalExtendedInventoryCache: Array<any> = [];
    public sectionPages: Array<any> = [];
    public pdfExportComponent: any = undefined;

    public constructor(props: any) {
        super(props, 'app.report-association.');

        this.state = {
            formChanges: {},
            inventoryChanges: [],
            reportPageCount: 0,
            showModalMissingValues: false,
            reportParameters: {
                fiscalYearPeriod: undefined,
                fiscalYearPeriod_reportCurrent: undefined, // new fiscal year (FY TEXT(H7, 'YYYY') - 1)
                reserveBalancesSufficient: false,

                reportApprovedAssessment: undefined,
                reportApprovedSpecialAssessment: undefined,
                reportReserveBalancesSufficient: undefined,
                reportProjectedFundBalanceAtFYE: undefined,
                reportApprovedReserveContribution: undefined,
                reportAnnualIncreases: undefined,
                reportNumYears: undefined,
                reportSecondaryIncrease: undefined,

                analysisFullyFundedBalance: undefined,
                analysisProjectedFundBalance: undefined,
                analysisProjectedPercentFundBalance: undefined,
                analysisFundingDeficiencyPerUnit: undefined,
                analysisNumberOfUnits: undefined,
                analysisInflationRate: undefined,
                analysisAfterTaxInterestRate: undefined,
                analysisRecommendedMRC: undefined,
                analysisRecommendedValues: undefined,
                analysisPreparedForFYE: undefined,

                approvedAssessment: undefined,
                estimatedReserveAmountRequired: undefined,
                projectedFundBalance: undefined,
                projectedFundPercent: undefined,
                assumptionInterest: undefined,
                assumptionInflation: undefined,
                reserveBalanceDeficiencyPerUnit: undefined,
                // lastReservePreparedDate: undefined,
                // reportDisclosureForFYEPeriodDate: undefined,
                // reportDate: undefined,
            },
            specialAssessments: [
                { id: 1, dueDate: 'N/A', amount: 'N/A', purpose: 'N/A' }
            ],
            additionalAssessments: [
                { id: 1, dueDate: 'N/A', amount: 'N/A' }
            ],
        };

        ReportCharts.setHighchartOptions();
    }

    public extendCurrentOrderUsers(currentOrder: any, userResumes: Array<any>) {
        const managerObjId = _.get(currentOrder, 'user.userObjectId', '');
        const manager = _.find(userResumes, ['user.userObjectId', managerObjId]);

        const siteInspectorObjId = _.get(currentOrder, 'siteInspector.userObjectId', '');
        const siteInspector = _.find(userResumes, ['user.userObjectId', siteInspectorObjId]);

        return {
            manager: manager ? _.cloneDeep(manager) : {},
            siteInspector: siteInspector ? _.cloneDeep(siteInspector) : {},
        };
    }

    public extendAssociationData(record: any) {
        if (!record) { return; }

        let result = _.cloneDeep(record);
        const invSubgroups = AppUtilities.getArray(record, 'inventorySubgroups');
        if (AppUtilities.isArrayWithLength(invSubgroups)) {
            // result.inventorySubgroups.push({ groupCode: '', title: '', label: this.appText('filter-option-any') });
            result.inventorySubgroups = result.inventorySubgroups.map(c => {
                if (!c.label) {
                    c.label = Formatters.composeInventorySubgroupLabel(c, true); // c.label = `${c.groupCode} - ${c.title}`;
                }

                return c;
            });
        }

        return result;
    }

    public extendInventoryData(inventory: Array<any>, inventoryChanges: Array<any>, association: any) {
        // Extend grid row data
        if (!AppUtilities.isArrayWithLength(inventory)) { return []; }

        const extended = inventory.map((r, i) => {
            let record = _.cloneDeep(r);

            record.avgCost = ReportLogic.calculateInventoryItemAvgCost(record);
            record.avgCost_sort = Number(record.avgCost);
            if (!record.avgCost) {
                GridUtilities.recordSetRowClass(record, 'row-no-avg-cost');
                record.usefulLife = 'N/A';
                record.remainingUsefulLife = 0;
            }

            const invSubgroups = AppUtilities.getArray(association, 'inventorySubgroups');
            record.Group = '';
            if (AppUtilities.isArrayWithLength(invSubgroups)) {
                let subgroupDef: any = _.find(invSubgroups, ['groupCode', _.get(record, 'subgroup', '')]);
                if (subgroupDef && subgroupDef.title) { record.Group = subgroupDef.title; }
            }

            record.usefulLife_sort = (AppUtilities.isNumeric(record.usefulLife)) ? Formatters.int(record.usefulLife) : 0;
            record.remainingUsefulLife_sort = (AppUtilities.isNumeric(record.remainingUsefulLife)) ? Formatters.int(record.remainingUsefulLife) : 0;

            record.included = _.get(record, 'included', (record.avgCost_sort !== 0 && record.usefulLife !== 'N/A') ? true : false);
            record.hypAvgCost = Formatters.float(_.get(record, 'hypAvgCost', record.avgCost_sort), 2);
            record.hypUsefulLife = Formatters.int(_.get(record, 'hypUsefulLife', record.usefulLife_sort));
            record.hypRemainingUsefulLife = Formatters.int(_.get(record, 'hypRemainingUsefulLife', record.remainingUsefulLife_sort));

            let invChanges = _.find(inventoryChanges, { inventoryId: record.inventoryId });
            if (invChanges) {
                record = _.merge(record, _.omit(invChanges, 'inventoryId'));
            }

            record.editing = record.included;

            return record;
        });

        if (this.originalExtendedInventoryCache.length === 0) { this.originalExtendedInventoryCache = _.cloneDeep(extended); }

        return extended;
    }

    public getFormData(currentOrderChart: any, currentOrder: any) {
        const currOrdChart = _.cloneDeep(currentOrderChart);
        const oldStyleSpecialAssessment = Number((_.get(currentOrderChart, 'sa', 0)).toFixed(2));
        const newStyleSpecialAssessments = _.get(currOrdChart, 'specialAssessments', []);
        const reportYear = _.get(currOrdChart, 'beginYear', 0);
        let assessmentToUse = 0;
        if (AppUtilities.isArrayWithLength(newStyleSpecialAssessments[0])) {
            if (_.toString(newStyleSpecialAssessments[0][0].year) === _.toString(reportYear)) {
                assessmentToUse = _.get(newStyleSpecialAssessments[0][0], 'amount', 0);
            }
        } else {
            assessmentToUse = oldStyleSpecialAssessment;
        }
        const result = {
            calculateYears: ReportCommon.CalculateYears, // Formatters.getInt(currentOrderChart, 'years', ReportCommon.CalculateYears),

            currentAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentAnnualIncrease', 5, 1, 0.03),
            currentMRCVal: Formatters.getFloat(currentOrderChart, 'currentMRCVal', 2),
            currentSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentSecondaryAnnualIncrease', 5, 1, 0.03),
            currentYears: Formatters.getInt(currentOrderChart, 'currentYears', ReportCommon.CalculateYears),

            minimumAnnualIncrease: Formatters.getFloat(currentOrderChart, 'minimumAnnualIncrease', 5, 1, 0.03),
            minimumMRCVal: Formatters.getFloat(currentOrderChart, 'minimumMRCVal', 2),
            minimumSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'minimumSecondaryAnnualIncrease', 5, 1, 0.03),
            minimumYears: Formatters.getInt(currentOrderChart, 'minimumYears', ReportCommon.CalculateYears),

            recommendedAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedAnnualIncrease', 5, 1, 0.03),
            recommendedMRCVal: Formatters.getFloat(currentOrderChart, 'recommendedMRCVal', 2),
            recommendedSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedSecondaryAnnualIncrease', 5, 1, 0.03),
            recommendedYears: Formatters.getInt(currentOrderChart, 'recommendedYears', ReportCommon.CalculateYears),

            // aka idealBalance
            currentFFB: Formatters.getFloat(currentOrderChart, 'ffb', 2) ? Formatters.getFloat(currentOrderChart, 'ffb', 2) : Formatters.getFloat(currentOrderChart, 'begBalance', 2),

            draft: _.get(currentOrderChart, 'draft', true) ? true : false,
            showMinimum: _.get(currentOrderChart, 'showMinimum', false) ? true : false,
            showRecommended: _.get(currentOrderChart, 'showRecommended', false) ? true : false,
            showCurrent: _.get(currentOrderChart, 'showCurrent', false) ? true : false,
            showPageNumbers: _.get(currentOrderChart, 'showPageNumbers', false) ? true : false,
            groupComponents: _.get(currentOrderChart, 'groupComponents', false) ? true : false,
            groupComponentsPictureSection: _.get(currentOrderChart, 'groupComponentsPictureSection', false) ? true : false,
            hideFooter: _.get(currentOrderChart, 'hideFooter', false) ? true : false,
            hideComponentEvaluation: _.get(currentOrderChart, 'hideComponentEvaluation', false) ? true : false,

            inflation: Formatters.getFloat(currentOrderChart, 'inflation', 5, 1), // 100),
            interest: Formatters.getFloat(currentOrderChart, 'interest', 5, 1), // 100),
            perFirstYear: Formatters.getInt(currentOrderChart, 'perFirstYear', 12),

            begBalance: Formatters.getFloat(currentOrderChart, 'begBalance', 2),
            beginYear: parseInt(_.get(currentOrderChart, 'beginYear', new Date().getFullYear()), 10),
            calculateYearsShort: 29, // parseInt(_.get(currentOrderChart, 'years', ReportCommon.CalculateYears), 10) - 1,
            mrcValue: Number((_.get(currentOrderChart, 'recommendedMRCVal', _.get(currentOrder, 'recommendedMRC', 0))).toFixed(2)),
            specialAssessment: Number(assessmentToUse),
            years: Number((_.get(currentOrderChart, 'recommendedYears', 0)).toFixed(0)),
            // fiscalYearPeriod: '', // `FY ${reportPeriodFYEDate.getFullYear() - 2}-${reportPeriodFYEDate.getFullYear() - 1}`;
        };

        // const newCurrentMRCVal = _.get(this.state, 'reportParameters.reportApprovedReserveContribution');

        return _.merge(result, this.state.formChanges);
    }

    public handleFormInputChange(e: any, x: any, unsetEmpty: boolean = false) {
        let fieldName: string = '';
        let newValue;

        let unsetStateVar: boolean = false;

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');

                // Unset empty text state vars
                unsetStateVar = (String(newValue) === '' && unsetEmpty) ? true : false;
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                case 'email':
                case 'password':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');

                    // Unset empty text state vars
                    unsetStateVar = (String(newValue) === '' && unsetEmpty) ? true : false;
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    break;
            }
        }

        this.setState((prevState, props) => {
            if (unsetStateVar) {
                _.unset(prevState, fieldName);
            } else {
                _.set(prevState, fieldName, newValue);
            }
            return prevState;
        });
    }

    public handleAdjustmentFormInputChange(n: any, v: any) {
        let changes = Object.assign({}, this.state.formChanges);
        changes[n] = Number(v);

        this.setState({ formChanges: changes });
    }

    public handleAdjustmentFormSubmit(e: any) {
        // e.preventDefault();
    }

    public handleItemChange(event: any) {
        const itemField = event.field;
        const itemValueNew = event.value;
        const itemValueOld = event.dataItem[itemField];
        const itemId = event.dataItem.inventoryId;
        const origItem = _.find(this.originalExtendedInventoryCache, { 'inventoryId': itemId });
        const origValue = _.get(origItem, itemField);

        let inv = _.cloneDeep(this.state.inventoryChanges);
        if (!_.find(inv, { 'inventoryId': itemId })) {
            const newItem = { inventoryId: itemId, };
            inv.push(newItem);
        }

        let item = _.find(inv, { 'inventoryId': itemId });
        if (item && _.get(item, itemField) !== itemValueNew) {
            if (itemValueNew === origValue) {
                // New value same as original, delete any change
                _.unset(item, itemField);
            } else {
                // New value differs from original
                _.set(item, itemField, itemValueNew);
            }

            if (_.keys(_.omit(item, 'inventoryId')).length === 0) {
                // No more changes for this item, remove it
                inv = _.reject(inv, { inventoryId: itemId });
            }

            _.set(event.dataItem, itemField, itemValueNew);

            this.setState({ inventoryChanges: inv });
        }
    }

    public getIsHypothetical() {
        const isHypothetical = (this.state.inventoryChanges.length === 0 && _.keys(this.state.formChanges).length === 0) ? false : true;

        return isHypothetical;
    }

    public getCompanyLogoByAssociation(association: any, location: string = Branding.appBrandLogoLocations.reportFooter) {
        const assocCompanyBrandKey = _.get(association, 'company.brandKey', Branding.appBrandKeys.complexSolutions);

        const path = `${assocCompanyBrandKey}.logo.${location}`;
        const result = _.get(AppImages, path, '');

        return result;
    }

    public getBrandingCssNameByAssociation(association: any) {
        const assocCompanyBrandKey = _.get(association, 'company.brandKey', Branding.appBrandKeys.complexSolutions);

        return assocCompanyBrandKey;
    }

    public registerSectionPageRef = (element: any, sectionName: string, pageIndex: number = 0) => {
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (existing) {
            if (_.get(existing, 'elementRef') !== element) {
                _.set(existing, 'elementRef', element);
            }
        }
    }

    public registerSectionPage = (sectionName: string, pageTitle: string = '', pageIndex: number = 0, includeInPageCount: boolean = true): number => {
        const eligiblePages = _.filter(this.sectionPages, (o) => _.get(o, 'includeInPageCount', true));
        const pageCount = _.size(eligiblePages);

        const pageNumber = (includeInPageCount) ? pageCount + 1 : 0;
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (!existing) {
            const page = {
                elementRef: undefined,
                includeInPageCount: includeInPageCount,
                pageKey: pageKey,
                pageNumber: pageNumber,
                pageTitle: pageTitle,
                sectionName: sectionName,
            };

            this.sectionPages.push(page);

            if (includeInPageCount) {
                this.setState({ reportPageCount: pageCount + 1 });
            }
        } else {
            return existing.pageNumber;
        }

        return pageNumber;
    }

    public getFirstPageOfSection = (secName: string): any => _(this.sectionPages).sortBy('pageNumber').find(['sectionName', secName]);

    public getPageNum = (secName: string) => _.get(this.getFirstPageOfSection(secName), 'pageNumber');

    public getPageRef = (secName: string) => _.get(this.getFirstPageOfSection(secName), 'elementRef');

    // HANDLERS
    public handlePrintPageButton(e: any, missing: boolean = true) {
        if (missing) {
            this.handleShowModalMissingValues();
        } else {
            window.print();
        }
    }

    public handleViewAssociationButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationDetail(associationId));
    }

    public handleViewReserveStudyButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.reserveStudy(associationId));
    }

    public handleScrollTo = (e: any, s: string = '') => {
        if (e) { e.preventDefault(); }

        scrollToComponent(this.getPageRef(s), CaliforniaDisclosureReportPdf.scrollToConfig);

        return false;
    }

    public handleShowModalMissingValues() {
        this.setState({ showModalMissingValues: true });
    }

    public handleCloseModalMissingValues() {
        this.setState({ showModalMissingValues: false });
    }

    // RENDERERS
    public renderHeaderButtonToolbar = (missingFV: boolean = true) => {
        let buttonProps = {
            bsSize: 'sm',
        };

        return (
            <div className="header-print-buttontoolbar-report">
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <Button {...buttonProps} bsStyle="info" onClick={(e) => { this.handlePrintPageButton(e, missingFV); }}>
                                    <i className="glyphicon glyphicon-print" /> {this.appTextCommon('buttons.print-document')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                        <Col xs={9}>
                            <ButtonToolbar>
                                <Button {...buttonProps} bsStyle="info" onClick={(e) => { this.handleViewAssociationButton(e); }}>
                                    <i className="glyphicon glyphicon-eye-open" /> {this.appTextCommon('buttons.view-association')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={(e) => { this.handleViewReserveStudyButton(e); }}>
                                    <i className="fa fa-line-chart" /> {this.appTextCommon('buttons.view-reserve-study')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public onCellEdit(row: any, fieldName: any, value: any, isSpecialAssessment: boolean) {
        if (isSpecialAssessment) {
            let sa = _.get(this.state, 'specialAssessments', []);

            const rowIdx = _.findIndex(sa, (o: any) => o.id === row.id);
            if (rowIdx < 0) { return value; }

            this.setState((state) => {
                _.set(state.specialAssessments[rowIdx], fieldName, value);
                return state;
            });

            return value;
        } else {
            let aa = _.get(this.state, 'additionalAssessments', []);

            const rowIdx = _.findIndex(aa, (o: any) => o.id === row.id);
            if (rowIdx < 0) { return value; }

            this.setState((state) => {
                _.set(state.additionalAssessments[rowIdx], fieldName, value);
                return state;
            });

            return value;

        }
    }

    public RenderDisclosureForm(st: any, reportData: any) {
        const stateReportParameters = _.get(reportData, 'reportParameters');

        // 20200116 const defaultInputProps = { colLabelXs: 8, colInputXs: 4 };
        const defaultInputProps = { colLabelXs: 7, colInputXs: 5 };

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v, true); },
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsCommon = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsDateInput = _.merge({}, inputPropsCommon, {
            // colLabelXs: 8, 20200116
            // colInputXs: 4, 20200116
            colLabelXs: 7,
            colInputXs: 5,
            format: 'M/dd/yyyy',
            formatPlaceholder: 'formatPattern',
        });

        let inputPropsReadOnly = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v, true); },
            className: 'inventory-detail-input-full-width',
            colLabelXs: 7,
            colInputXs: 5
        });

        let alertReportDate = (<div />);
        let aReportDate = _.get(stateReportParameters, 'reportLastReservePreparedDate');
        if (aReportDate === undefined) {
            alertReportDate = (
                <ReportComponents.WarningAlert
                    title={this.appText('page.ca-disclosure-form.alert.value-missing')}
                    body={this.appText('page.ca-disclosure-form.alert.value-missing.report-date')}
                />
            );
        }

        let alertApprovedReserveContribution = (<div />);
        const aApprovedReserveContribution = Formatters.getFloat(reportData, 'reportParameters.reportApprovedReserveContribution');
        if (aApprovedReserveContribution === 0) {
            alertApprovedReserveContribution = (
                <ReportComponents.WarningAlert
                    title={this.appText('page.ca-disclosure-form.alert.value-missing')}
                    body={this.appText('page.ca-disclosure-form.alert.value-missing.approved-reserve-contribution')}
                />
            );
        }

        let alertSpecialAssessmentInvolved = (<div />);
        const aSpecialAssessmentInvolved = Formatters.getFloat(reportData, 'currentOrderChart.sa');
        if (isNullOrUndefined(aSpecialAssessmentInvolved)) {
            // null or undefined recommended special assessment -- do nothing
        } else if (aSpecialAssessmentInvolved !== 0 && aSpecialAssessmentInvolved !== _.get(reportData, 'reportParameters.reportApprovedSpecialAssessment')) {
            alertSpecialAssessmentInvolved = (
                <ReportComponents.WarningAlert
                    title={this.appText('page.ca-disclosure-form.alert.special-assessment-involved')}
                    body={(_.get(reportData, 'currentOrderChart.sa') !== _.get(reportData, 'reportParameters.reportApprovedSpecialAssessment')) ?
                        this.appText('page.ca-disclosure-form.alert.special-assessment-involved.value-mismatch') :
                        this.appText('page.ca-disclosure-form.alert.special-assessment-involved.exercise-caution')
                    }
                />
            );
        }

        const approvedSpecialAssessment = Formatters.getFloat(reportData, 'currentOrderChart.currentSA');
        if (!isNullOrUndefined(approvedSpecialAssessment) && isNullOrUndefined(this.state.reportParameters.reportApprovedSpecialAssessment)) {
            this.setState({ reportParameters: { reportApprovedSpecialAssessment: Formatters.getFloat(reportData, 'currentOrderChart.currentSA') } });
        }

        return (
            <DisclosureFormSection title={this.appText('page.ca-disclosure-form.customize-disclosure-form')}>
                <KendoWrappers.KendoFormSectionTitled title={this.appText('page.ca-disclosure-form.disclosure-variables')}>
                    <Row>
                        <Col xs={6}>
                            <div>
                                <KendoWrappers.KendoTextInput
                                    name="reportParameters.reportApprovedAssessment"
                                    label={this.appText('page.ca-disclosure-form.approved-assessment')}
                                    value={Formatters.getStr(stateReportParameters, 'reportApprovedAssessment')}
                                    clearable={0}
                                    {...inputPropsCommon}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoTextInput
                                    name="reportParameters.reportExhibitText"
                                    label={this.appText('page.ca-disclosure-form.exhibit-text')}
                                    value={Formatters.getStr(stateReportParameters, 'reportExhibitText')}
                                    placeholder="Leave Blank for N/A"
                                    clearable={0}
                                    {...inputPropsCommon}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDateInput
                                    name="reportParameters.reportLastReservePreparedDate"
                                    label={this.appText('page.ca-disclosure-form.report-date')}
                                    required={true}
                                    clearable={0}
                                    min={Formatters.formatDateForKendoDateInput(MinimumReportDate())}
                                    value={_.get(stateReportParameters, 'reportLastReservePreparedDate')}
                                    {...inputPropsDateInput}
                                />

                                {alertReportDate}
                            </div>

                            <div>
                                <KendoWrappers.KendoDateInput
                                    name="reportParameters.reportDisclosureForFYEPeriodDate"
                                    label={this.appText('page.ca-disclosure-form.disclosure-for-fye-period')}
                                    clearable={0}
                                    value={Formatters.formatDateForKendoDateInput(_.get(stateReportParameters, 'reportDisclosureForFYEPeriodDate'))}
                                    {...inputPropsDateInput}
                                />
                            </div>

                            {<div>
                                <KendoWrappers.KendoCheckbox
                                    name="reportParameters.reportReserveBalancesSufficient"
                                    label={this.appText('page.ca-disclosure-form.reserve-balances-sufficient')}
                                    value={_.get(stateReportParameters, 'reportReserveBalancesSufficient')}
                                    {...inputPropsCommon}
                                />
                            </div>}

                            <div>
                                <KendoWrappers.KendoCurrencyInputNew
                                    name="reportParameters.reportProjectedFundBalanceAtFYE"
                                    label={this.appText('page.ca-disclosure-form.projected-balance-at-fye')}
                                    defaultValue={Formatters.getFloat(reportData, 'reportParameters.reportProjectedFundBalanceAtFYE')}
                                    clearable={0}
                                    min={-999999999}
                                    {...inputPropsCommon}
                                />
                            </div>
                        </Col>

                        <Col xs={6}>
                            <div>
                                <KendoWrappers.KendoCurrencyInputNew
                                    name="reportParameters.reportApprovedReserveContribution"
                                    label={this.appText('page.ca-disclosure-form.approved-reserve-contribution')}
                                    value={Formatters.getFloat(reportData, 'reportParameters.reportApprovedReserveContribution')}
                                    // defaultValue={Formatters.getFloat(reportData, 'reportParameters.reportApprovedReserveContribution')}
                                    // 20200116 defaultValue={Formatters.getFloat(reportData, 'reportParameters.reportApprovedReserveContribution')}
                                    clearable={0}
                                    min={-999999999}
                                    step={10}
                                    required={true}
                                    {...inputPropsCommon}
                                />

                                {alertApprovedReserveContribution}
                            </div>

                            <div>
                                <KendoWrappers.KendoPercentageInput
                                    name="reportParameters.reportAnnualIncreases"
                                    label={this.appText('page.ca-disclosure-form.annual-pct-increases')}
                                    min={0}
                                    step={.0025}
                                    value={Formatters.getFloat(reportData, 'reportParameters.reportAnnualIncreases', 5)}
                                    {...inputPropsCommon}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="reportParameters.reportNumYears"
                                    label={this.appText('page.ca-disclosure-form.num-of-years')}
                                    defaultValue={Formatters.getInt(reportData, 'reportParameters.reportNumYears')}
                                    clearable={0}
                                    min={0}
                                    step={1}
                                    {...inputPropsCommon}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoPercentageInput
                                    name="reportParameters.reportSecondaryIncrease"
                                    label={this.appText('page.ca-disclosure-form.second-increase-pct')}
                                    min={0}
                                    step={.0025}
                                    value={Formatters.getFloat(reportData, 'reportParameters.reportSecondaryIncrease', 5)}
                                    {...inputPropsCommon}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCurrencyInputNew
                                    name="reportParameters.reportApprovedSpecialAssessment"
                                    label={this.appText('page.ca-disclosure-form.client-approved-special-assessment')}
                                    value={Formatters.getFloat(stateReportParameters, 'reportApprovedSpecialAssessment')}
                                    clearable={0}
                                    min={0}
                                    step={10}
                                    {...inputPropsCommon}
                                />
                                {alertSpecialAssessmentInvolved}
                            </div>
                        </Col>
                    </Row>
                </KendoWrappers.KendoFormSectionTitled>

                <KendoWrappers.KendoFormSectionTitled title={this.appText('page.ca-disclosure-form.report-parameters')}>
                    <Row>
                        <Col xs={6}>
                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisFullyFundedBalance"
                                    label={this.appText('page.ca-disclosure-form.fully-funded-balance')}
                                    value={(<ReportComponents.Currency value={Formatters.getFloat(stateReportParameters, 'analysisFullyFundedBalance')} />)}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisProjectedFundBalance"
                                    label={this.appText('page.ca-disclosure-form.projected-fund-balance')}
                                    value={(<ReportComponents.Currency value={Formatters.getFloat(stateReportParameters, 'analysisProjectedFundBalance')} />)}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisProjectedPercentFundBalance"
                                    label={this.appText('page.ca-disclosure-form.projected-pct-funded-at-fye')}
                                    value={(<ReportComponents.Percentage value={Formatters.getFloat(stateReportParameters, 'analysisProjectedPercentFundBalance', 5)} decimalsParse={5} decimals={2} />)}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisFundingDeficiencyPerUnit"
                                    label={this.appText('page.ca-disclosure-form.funding-deficiency-per-unit')}
                                    value={(<ReportComponents.Currency value={Formatters.getFloat(stateReportParameters, 'analysisFundingDeficiencyPerUnit')} />)}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                        </Col>

                        <Col xs={6}>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisRecommendedMRC"
                                    label={this.appText('page.ca-disclosure-form.recommended-mrc')}
                                    value={(<ReportComponents.Currency value={Formatters.getFloat(stateReportParameters, 'analysisRecommendedMRC')} />)}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisRecommendedValues"
                                    label={this.appText('page.ca-disclosure-form.first-pct-num-years-second-pct')}
                                    value={(
                                        <div>
                                            <span className="report-funding-disclosure-parameter-value">
                                                <ReportComponents.Percentage
                                                    value={Formatters.getFloat(stateReportParameters, 'analysisRecommendedAnnualIncreases', 5)}
                                                    decimals={3}
                                                    decimalsParse={5}
                                                />,
                                            </span>
                                            <span className="report-funding-disclosure-parameter-value">
                                                <ReportComponents.PrecisionNumber
                                                    value={Formatters.getInt(stateReportParameters, 'analysisRecommendedYears')}
                                                    decimals={0}
                                                />,
                                            </span>
                                            <span className="report-funding-disclosure-parameter-value">
                                                <ReportComponents.Percentage
                                                    value={Formatters.getFloat(stateReportParameters, 'analysisRecommendedSecondaryIncrease', 5)}
                                                    decimals={3}
                                                    decimalsParse={5}
                                                />
                                            </span>
                                        </div>
                                    )}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisRecommendedSA"
                                    label={this.appText('page.ca-disclosure-form.recommended-sa')}
                                    value={(<ReportComponents.Currency value={Formatters.getFloat(stateReportParameters, 'reportRecommendedSpecialAssessment')} />)}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisPreparedForFYE"
                                    label={this.appText('page.ca-disclosure-form.report-prepared-for-fye')}
                                    value={ReportComponents.shortDate(_.get(stateReportParameters, 'analysisPreparedForFYE'))}
                                    {...inputPropsReadOnly}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="reportParameters.analysisNumberOfUnits"
                                    label={this.appText('page.ca-disclosure-form.num-units')}
                                    value={Formatters.getStr(stateReportParameters, 'analysisNumberOfUnits')}
                                    {...inputPropsReadOnly}
                                />
                            </div>
                        </Col>
                    </Row>
                </KendoWrappers.KendoFormSectionTitled>
            </DisclosureFormSection>
        );
    }

    public renderMissingValuesModal = () => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        return (
            <Modal enforceFocus={false} show={this.state.showModalMissingValues} onHide={() => this.handleCloseModalMissingValues()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('page.ca-disclosure-form.modals.missing-values.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className="calculate-prices-modal-content">
                        <strong>{this.appText('page.ca-disclosure-form.modals.missing-values.header')}</strong>
                    </p>

                    <p className="calculate-prices-modal-content">
                        {this.appText('page.ca-disclosure-form.modals.missing-values.content')}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalMissingValues()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { currentOrder, currentOrderChart, associationId } = this.props;

        const association = this.extendAssociationData(this.props.association);
        const inventory: Array<any> = this.extendInventoryData(this.props.inventory, this.state.inventoryChanges, association);

        let cloneCurrentOrderChart = _.cloneDeep(currentOrderChart);
        let cloneCurrentOrder = _.cloneDeep(currentOrder);

        const isLoading = Number(Formatters.getInt(association, 'associationId')) !== Number(associationId);
        const noChartData = _.isEmpty(currentOrder) ||
            _.isEmpty(currentOrderChart) ||
            _.isEmpty(association) ||
            _.isEmpty(inventory);

        let page = (<div />);
        let brandingCssName = '';

        let hasComponentsWithNegativeRUL = (_.find(inventory, (o) => Formatters.getInt(o, 'remainingUsefulLife_sort') < 0)) ? true : false;

        if (!isLoading) {
            if (noChartData) {
                page = (
                    <div>
                        <Row>
                            <Col xs={4}>
                                <Alert bsStyle="info">
                                    <h4>{this.appText('chart-data-missing')}</h4>
                                    <p>{this.appText('chart-data-missing-body')}</p>
                                </Alert>
                            </Col>
                        </Row>
                    </div>
                );
            } else if (hasComponentsWithNegativeRUL) {
                page = (
                    <div className="association-inventory-alert-component-negative-rul">
                        <Alert bsStyle="warning">
                            <h4><i className="fa fa-exclamation-triangle" /> {this.appText('alerts.warn-component-negative-rul.title')}</h4>
                            <p>{this.appText('alerts.warn-component-negative-rul.body')}</p>
                        </Alert>
                    </div>
                );
            } else {
                let currentOrderUsers = this.extendCurrentOrderUsers(cloneCurrentOrder, this.props.userResumes);

                const isHypothetical = this.getIsHypothetical();

                const formData = this.getFormData(cloneCurrentOrderChart, cloneCurrentOrder);

                const repParamsStateClone = _.get(this.state, 'reportParameters', {});

                const fdMerge = {
                    caDiscAnnualIncrease: Formatters.getFloat(repParamsStateClone, 'reportAnnualIncreases', 5, 1, Formatters.getFloat(currentOrderChart, 'currentAnnualIncrease', 5, 1, 0.03)),
                    caDiscMRCVal: _.get(repParamsStateClone, 'reportApprovedReserveContribution', 0),
                    caDiscSecondaryAnnualIncrease: Formatters.getFloat(repParamsStateClone, 'reportSecondaryIncrease', 5, 1, Formatters.getFloat(currentOrderChart, 'currentSecondaryAnnualIncrease', 5, 1, 0.03)),
                    caDiscYears: Formatters.getInt(repParamsStateClone, 'reportNumYears', Formatters.getInt(currentOrderChart, 'currentYears', ReportCommon.CalculateYears)),
                    caDiscApprovedSpecialAssessment: _.get(repParamsStateClone, 'reportApprovedSpecialAssessment', 0),
                };

                const mergedCurrentOrderChart = _.merge({}, cloneCurrentOrderChart, formData, fdMerge);

                const orderChartBeginYear = _.get(mergedCurrentOrderChart, 'beginYear');
                const assocFiscalYearEnd = _.get(association, 'details.fiscalYearEnd');
                const perFirstYear = Number(_.get(mergedCurrentOrderChart, 'perFirstYear', 12));
                const fyeDateStr = `${orderChartBeginYear}/${assocFiscalYearEnd}`;
                const dateEndAssocFYE = new Date(fyeDateStr);
                const reportPeriodEndDateMoment = moment(dateEndAssocFYE).toObject();
                const reportPeriodEndDate = moment(reportPeriodEndDateMoment); // .subtract(1, 'day').toObject();
                const reportPeriodStartDate = moment(reportPeriodEndDateMoment).subtract(perFirstYear, 'month').add(1, 'day').toObject();

                const calculationResult = ReportLogic.computeFundingCalculations(mergedCurrentOrderChart, cloneCurrentOrder, inventory, ReportCommon.CalculateYears);

                const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
                const invMap = AppUtilities.getArray(calculationResult, 'invMap');

                const percentFunded = ReportLogic.getPercentFunded(calculations, isHypothetical);

                // REPORT PAGES CONFIGURATION
                const pageConfiguration = {
                    maxTableRowsPerPage: 24, // Causes page breaks if a table exceeds this # rows
                    maxTableRowsPerFirstPage: 18, // Causes page breaks if a table exceeds this # rows
                    maxColumnsPerTable: 3, // Used on yearly cash flow, etc
                    charactersPerLine: 110,
                    kendoCharts: {
                        renderAs: 'svg',
                    },
                    charts: {
                        xAxis: {
                            title: {
                                x: -40,
                                y: 20,
                            },
                        },
                        legend: {
                            verticalAlign: 'bottom',
                            align: 'center',
                            x: 0,
                            y: 0,
                        },
                        chart: {
                            height: 600
                        }
                    }
                };

                // YEARLY SUMMARY DATA
                const yearlySummaryData = ReportCharts.getYearlySummaryGridData((e) => this.appText(e), calculations, formData.beginYear, formData.calculateYears, isHypothetical);

                // COMPONENT EVALUATIONS
                const isComponentEvaluationHidden = (_.get(mergedCurrentOrderChart, 'hideComponentEvaluation', false)) ? true : false;
                const hideComponentEvaluation = (isLevel3Report(currentOrder) || isComponentEvaluationHidden) ? true : false;
                const showComponentEvaluation = (hideComponentEvaluation) ? false : true;

                // REPORT FOOTER
                const showReportPageFooter = !((_.get(mergedCurrentOrderChart, 'hideFooter', false)) ? true : false);

                // Limit to 30 years
                const yearlySummaryDataCorrected = _.take(yearlySummaryData, formData.calculateYears);
                const calculationsCorrected = _.take(calculations, formData.calculateYears);

                // Ideal/Projected balances
                const yearToUse = (_.get(this.state.reportParameters, 'reportDisclosureForFYEPeriodDate')) ? _.get(this.state.reportParameters, 'reportDisclosureForFYEPeriodDate').getFullYear() : _.get(formData, 'beginYear');
                const yearSummary = _.find(yearlySummaryDataCorrected, ['year', yearToUse]);
                const startingBalance = _.toNumber(_.get(currentOrder, 'startingBalance', 0));
                const begBalance = _.toNumber(_.get(currentOrderChart, 'begBalance', startingBalance));
                const idealBalance = _.toNumber(_.get(yearSummary, 'r_ffb', begBalance));

                // Report Exhibit Text
                const reportExhibitText = _.get(this.state.reportParameters, 'reportExhibitText', '');

                // FOOTER DATA
                const reportData = {
                    association: association,
                    showComponentEvaluation: showComponentEvaluation,
                    showFooterFull: showReportPageFooter,
                    branding: {
                        // logoCoverPage: this.getCompanyLogo(currentOrder, Branding.appBrandLogoLocations.reportCoverPage),
                        // logoCoverPage: this.getCompanyLogo(currentOrder, Branding.appBrandLogoLocations.reportCoverPage),
                        logoCoverPage: this.getCompanyLogoByAssociation(association, Branding.appBrandLogoLocations.reportCoverPage),
                        logoFooter: this.getCompanyLogoByAssociation(association, Branding.appBrandLogoLocations.reportFooter),
                    },
                    calculations: calculationsCorrected, // calculations,
                    currentOrder: currentOrder,
                    currentOrderChart: mergedCurrentOrderChart, // currentOrderChart,
                    mergedCurrentOrderChart: mergedCurrentOrderChart,
                    currentOrderUsers: currentOrderUsers,
                    formData: formData,
                    inventory: invMap,
                    isHypothetical: isHypothetical,
                    percentFunded: percentFunded,
                    yearlySummaryData: yearlySummaryDataCorrected, // yearlySummaryData,
                    reportPeriodStartDate: reportPeriodStartDate,
                    reportPeriodEndDate: reportPeriodEndDate,
                    reportPeriodFYEDate: dateEndAssocFYE,
                    reportBeginYearSummary: yearSummary,
                    reportBeginYear: formData.beginYear,
                    reportBeginYearIdealBalance: idealBalance,
                    reportBeginYearStartBalance: begBalance,
                    reportDisclosureYears: 6,
                    reportExhibitText: reportExhibitText,
                    reportParameters: {},
                    assocNumUnits: Formatters.getInt(association, 'details.numberUnits'),
                    balanceDeficiency: idealBalance - begBalance,
                };

                const repParamsDefault: any = (date: Date, ySummary: any, mOrdChart: any, repData: any) => {
                    const orig = {
                        fiscalYearPeriod: `FY ${date.getFullYear() - 2}-${date.getFullYear() - 1}`,
                        reserveBalancesSufficient: false,
                        approvedAssessment: '$0.00', // Formatters.getFloat(mOrdChart, 'currentMRCVal'), // 20200117 Changed from '$0.00',
                        estimatedReserveAmountRequired: Formatters.getFloat(ySummary, 'r_ffb', 2, 1),
                        projectedFundBalance: Formatters.getFloat(ySummary, 'r_rb', 2, 1),
                        projectedFundPercent: Formatters.getFloat(ySummary, 'r_pf', 5, 1),
                        assumptionInterest: Formatters.getFloat(mOrdChart, 'interest', 5, 1),
                        assumptionInflation: Formatters.getFloat(mOrdChart, 'inflation', 5, 1),
                    };

                    const defaultReportDisclosureForFYEPeriodDate = moment(date).toDate();

                    const res = Object.assign(
                        {},
                        orig,
                        {
                            reportApprovedAssessment: '$0.00', // Formatters.getFloat(repData, 'currentOrderChart.currentMRCVal', 2), // 20200117 Was '$0.00',
                            reportApprovedSpecialAssessment: (Formatters.getFloat(repData, 'currentOrderChart.currentSA', 2)) ? Formatters.getFloat(repData, 'currentOrderChart.currentSA', 2) : 0.00,
                            reportDate: ReportComponents.monthDate(Date.now()),
                            reportLastReservePreparedDate: undefined, // DefaultReportDate(), // MinimumReportDate(), // undefined, // 20200116 Rob request - blank field DefaultLastReportDate(),
                            reportDisclosureForFYEPeriodDate: defaultReportDisclosureForFYEPeriodDate, // undefined // `FY ${date.getFullYear() - 2}-${date.getFullYear() - 1}`, // undefined,  // 20191009 configured
                            reportReserveBalancesSufficient: Formatters.getFloat(repData, 'currentOrderChart.sa', 5) ? false : true,
                            reportProjectedFundBalanceAtFYE: Formatters.getFloat(ySummary, 'r_rb', 2, 1),
                            reportApprovedReserveContribution: 0, // 20160116 Rob request - blank field Formatters.getFloat(formData, 'currentMRCVal'),
                            reportAnnualIncreases: Formatters.getFloat(repData, 'currentOrderChart.currentAnnualIncrease', 5),
                            // reportRecommendedSpecialAssessment: Formatters.getFloat(repData, 'currentOrderChart.sa', 5), // 20230516 Becca Request to fix missing special assessment in recommended special assessment field
                            reportRecommendedSpecialAssessment: Formatters.getFloat(formData, 'specialAssessment', 5),
                            // reportAnnualIncreases:  Formatters.getFloat(formData, 'currentAnnualIncrease', 5),
                            reportNumYears: Formatters.getInt(formData, 'currentYears'),
                            // reportSecondaryIncrease: Formatters.getFloat(formData, 'currentSecondaryAnnualIncrease', 5),
                            reportSecondaryIncrease: Formatters.getFloat(formData, 'currentSecondaryAnnualIncrease', 5),

                            analysisFullyFundedBalance: Formatters.getFloat(ySummary, 'ffb'),
                            analysisProjectedFundBalance: Formatters.getFloat(ySummary, 'rb'),
                            analysisProjectedPercentFundBalance: Formatters.getFloat(ySummary, 'pf', 5),
                            analysisFundingDeficiencyPerUnit: (Formatters.getFloat(ySummary, 'ffb') - Formatters.getFloat(ySummary, 'rb', 2, 1)) / Formatters.getFloat(repData, 'assocNumUnits'), // (ffb - h_rb)/numUnits
                            analysisNumberOfUnits: Formatters.getInt(repData, 'assocNumUnits'),
                            // analysisInflationRate: Formatters.getFloat(formData, 'interest', 5),
                            analysisInflationRate: Formatters.getFloat(repData, 'currentOrderChart.inflation', 5),
                            // analysisAfterTaxInterestRate: Formatters.getFloat(formData, 'inflation', 5),
                            analysisAfterTaxInterestRate: Formatters.getFloat(repData, 'currentOrderChart.interest', 5),
                            analysisRecommendedMRC: Formatters.getFloat(ySummary, 'r_mrc'),
                            // analysisRecommendedAnnualIncreases: Formatters.getFloat(formData, 'recommendedAnnualIncrease', 5),
                            analysisRecommendedAnnualIncreases: Formatters.getFloat(repData, 'currentOrderChart.recommendedAnnualIncrease', 5, 1),
                            analysisRecommendedYears: Formatters.getInt(formData, 'recommendedYears'),
                            // analysisRecommendedSecondaryIncrease: Formatters.getFloat(formData, 'recommendedSecondaryAnnualIncrease', 5),
                            analysisRecommendedSecondaryIncrease: Formatters.getFloat(repData, 'currentOrderChart.recommendedSecondaryAnnualIncrease', 5, 1),
                            analysisRecommendedValues: '',
                            analysisPreparedForFYE: defaultReportDisclosureForFYEPeriodDate,
                        });

                    return res;
                };

                const repParamsState = _.get(this.state, 'reportParameters', {});
                const repParamsDefaultData = repParamsDefault(dateEndAssocFYE, yearSummary, mergedCurrentOrderChart, reportData);
                const repParamsMerged = _.merge({}, repParamsDefaultData, repParamsState);

                const fbfye = Formatters.getFloat(repParamsMerged, 'reportProjectedFundBalanceAtFYE');
                const affb = Formatters.getFloat(repParamsMerged, 'analysisFullyFundedBalance');
                const appfb = fbfye / affb;
                repParamsMerged.analysisProjectedPercentFundBalance = appfb;

                repParamsMerged.analysisFundingDeficiencyPerUnit = (affb - fbfye) / Formatters.getFloat(repParamsMerged, 'analysisNumberOfUnits');
                reportData.reportParameters = repParamsMerged;

                // RENDERING
                brandingCssName = this.getBrandingCssNameByAssociation(association);

                const createCustomButtonGroup = (props) => {
                    return (
                        <ButtonGroup bsSize="xs">
                            {props.insertBtn}
                            {props.deleteBtn}
                        </ButtonGroup>
                    );
                };

                const headerColProps: any = {
                    headerAlign: 'center',
                    dataAlign: 'center',
                };

                const tableProps: any = {
                    options: { btnGroup: createCustomButtonGroup },
                    cellEdit: { mode: 'click' },
                    insertRow: true,
                    deleteRow: false,
                    tableHeaderClass: 'th-edit',
                };

                const currencyFormatter = (c: any, r: any) => <ReportComponents.Currency value={c} decimals={0} />;
                const percentFormatter = (c: any, r: any) => <ReportComponents.Percentage value={c} decimals={0} />;
                const dateFormatter = (c: any, r: any) => Formatters.formatDateShortDate(c);

                const dateColumnProps: any = {
                    editable: { type: 'date' },
                    dataFormat: dateFormatter,
                };

                const textColumnProps: any = {
                    editable: { type: 'textarea' },
                };

                const idColumnProps: any = {
                    hidden: true,
                    hiddenOnInsert: true,
                    isKey: true,
                    dataField: 'id',
                    autoValue: true,
                };

                const onAddRow = (row: any) => {
                    let sa = this.state.specialAssessments;
                    sa.push(row);

                    this.setState({ specialAssessments: sa });
                };

                const onDeleteRow = (row: any) => {
                    let sa = this.state.specialAssessments;

                    let saFiltered = sa.filter((o) => o.id !== row[0]);

                    // this.setState({ specialAssessments: saFiltered });
                };

                const onAddRowAdditional = (row: any) => {
                    let aa = this.state.additionalAssessments;
                    aa.push(row);
                    this.setState({ additionalAssessments: aa });
                };

                const onDeleteRowAdditional = (row: any) => {
                    let aa = this.state.additionalAssessments;

                    let aaFiltered = aa.filter((o) => o.id !== row[0]);

                    // this.setState({ specialAssessments: saFiltered });
                };

                let tableSpecialAssessments = (
                    <div>
                        <BootstrapTable
                            {..._.merge(
                                {},
                                tableProps,
                                {
                                    options: {
                                        insertText: this.appText('page.ca-disclosure-form.add-assessment'), // 'Add Assessment',
                                        onCellEdit: (r: any, f: any, v: any) => { this.onCellEdit(r, f, v, true); },
                                        onDeleteRow: onDeleteRow,
                                        onAddRow: onAddRow
                                    }
                                })}
                            data={this.state.specialAssessments}
                        >
                            <TableHeaderColumn {...idColumnProps}>ID</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} {...textColumnProps} dataField="dueDate">Date assessment will be due</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} {...textColumnProps} dataField="amount">Amount per ownership interest per month or year (If assessments are variable, see note immediately below)</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} {...textColumnProps} dataField="purpose">Purpose of the Assessment</TableHeaderColumn>
                        </BootstrapTable>
                    </div>);

                let tableAdditionalAssessments = (
                    <div>
                        <BootstrapTable
                            {..._.merge(
                                {},
                                tableProps,
                                {
                                    options: {
                                        insertText: this.appText('page.ca-disclosure-form.add-assessment'), /* 'Add Assessment' */
                                        onCellEdit: (r: any, f: any, v: any) => { this.onCellEdit(r, f, v, false); },
                                        onDeleteRow: onDeleteRowAdditional,
                                        onAddRow: onAddRowAdditional
                                    }
                                })} data={this.state.additionalAssessments}>
                            <TableHeaderColumn {...idColumnProps}>ID</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} {...textColumnProps} dataField="dueDate">Approximate date assessment will be due</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} {...textColumnProps} dataField="amount">Amount per ownership interest per month or year</TableHeaderColumn>
                        </BootstrapTable>
                    </div>);

                let yearSlice = 1;

                let repDateDiffers = false;
                const repDiscFYEDate = _.get(repParamsMerged, 'reportDisclosureForFYEPeriodDate');
                if (repDiscFYEDate) {
                    const discFYEDate = moment(repDiscFYEDate);
                    const found = _.findIndex(yearlySummaryDataCorrected, ['year', discFYEDate.year()]);
                    if (found >= 0) { yearSlice = found + 1; }

                    const repAnalysisPreparedForFYE = _.get(repParamsMerged, 'analysisPreparedForFYE');
                    if (repAnalysisPreparedForFYE) {
                        const analysisFYEDate = moment(repAnalysisPreparedForFYE);

                        if (!analysisFYEDate.isSame(discFYEDate)) {
                            repDateDiffers = true;
                        }
                    }
                }

                const calcFV = (rate: number, nPer: number, pmt: number = 0, pv: number = 0, type: number = 0) => {
                    let pow: number = Math.pow(1 + rate, nPer);
                    let fv: number = 0;
                    if (rate) {
                        fv = (pmt * (1 + rate * type) * (1 - pow) / rate) - pv * pow;
                    } else {
                        fv = -1 * (pv + pmt * nPer);
                    }

                    return fv;
                };

                const floatCompare = (f1: number, f2: number, tolerance: number = 0.0001) => {
                    let res = false;
                    if (Math.abs(f1 - f2) < tolerance) {
                        res = true;
                    }
                    return res;
                };

                const ysdSliced = _.slice(yearlySummaryDataCorrected, yearSlice, (yearSlice + 5)); // 20200128 was: reportData.reportDisclosureYears));
                const dataYearSummary = _.map(ysdSliced, (ysd: any, ysdIdx: any) => {
                    let res = {
                        id: ysd.year,
                        fyeEnd: ysd.year - 1,
                        rsProjEndBal: Formatters.getFloat(ysd, 'r_rb', 2),
                        rsProjIdealBal: Formatters.getFloat(ysd, 'r_ffb', 2),
                        rsProjPctFund: Formatters.getFloat(ysd, 'r_pf', 2),
                        afpProjEndBal: Formatters.getFloat(ysd, 'caDisc_rb', 2),
                        afpPctFund: Formatters.getFloat(ysd, 'caDisc_pf', 2),
                    };
                    return res;
                });

                let tableYearSummary = (
                    <div>
                        <BootstrapTable {..._.merge({}, tableProps, { options: { insertText: this.appText('page.ca-disclosure-form.add-year-calculation') } })} data={dataYearSummary}>
                            <TableHeaderColumn {...idColumnProps}>ID</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} dataField="fyeEnd">End of Fiscal Year</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} dataFormat={currencyFormatter} dataField="rsProjEndBal">Projected End Balance (Per Reserve Study)</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} dataFormat={currencyFormatter} dataField="rsProjIdealBal">Projected Ideal Balance (Per Reserve Study)</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} dataFormat={percentFormatter} dataField="rsProjPctFund">Projected Percent Funded (Per Reserve Study)</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} dataFormat={currencyFormatter} dataField="afpProjEndBal">Projected End Balance (Approved Funding Plan)</TableHeaderColumn>
                            <TableHeaderColumn {...headerColProps} dataFormat={percentFormatter} dataField="afpPctFund">Percent Funded (Approved Funding Plan)</TableHeaderColumn>
                        </BootstrapTable>
                    </div>);

                let missingFormValues: boolean = false;
                const stateReportParameters = _.get(reportData, 'reportParameters');
                let aReportDate = _.get(stateReportParameters, 'reportLastReservePreparedDate');
                if (!aReportDate) { missingFormValues = true; }

                const aApprovedReserveContribution = Formatters.getFloat(reportData, 'reportParameters.reportApprovedReserveContribution');
                if (aApprovedReserveContribution === 0) { missingFormValues = true; }

                page = (
                    <div>
                        {this.renderMissingValuesModal()}
                        {this.renderHeaderButtonToolbar(missingFormValues)}
                        <div>
                            <div className="header-print-buttontoolbar-report pdf-disclosure-page-1-section-form">
                                <Well className="header-well-buttontoolbar" >
                                    {this.RenderDisclosureForm(this.state, reportData)}
                                </Well>
                            </div>
                        </div>
                        <Row className="pdf-report-container">
                            <Col xs={12}>
                                <div className="pdf-report pdf-reserve-study-report center-block">
                                    <div className="pdf-reserve-study-report-container pdf-ca-disclosure-report-container">
                                        <DisclosurePage1 reportData={reportData} tableSpecial={tableSpecialAssessments} tableAdditional={tableAdditionalAssessments} />
                                        <DisclosurePage2 reportData={reportData} tableYearSummary={tableYearSummary} />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            }
        }

        const { ...rest } = this.props;
        return (
            <div className={brandingCssName}>
                <AppComp.RenderPageWithLayout branding={this.props.branding} pageName="" {...rest}>
                    {page}
                </AppComp.RenderPageWithLayout>
            </div>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getReportForAssociationUrl(props.match.params.associationId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getReportForAssociationRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const assocId = props.match.params.associationId;
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        association: state.entities.reportAssociation,
        associationId: assocId,

        currentOrder: state.entities.reportAssociationCurrentOrder,
        currentOrderChart: state.entities.reportAssociationCurrentOrderChart,
        inventory: state.entities.reportAssociationInventory,
        scenarios: state.entities.reportAssociationScenarios,
        userResumes: state.entities.reportAssociationUserResumes,
    });
};

const mapDispatchToProps = {
    navigateTo: (path) => push(path),
};

const CaliforniaDisclosureReportPdfContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(CaliforniaDisclosureReportPdf);

export default withRouter(CaliforniaDisclosureReportPdfContainer);



// WEBPACK FOOTER //
// ./src/report/pdf/ca-disclosure-pdf.tsx